import { useCallback, useState, MouseEvent, useRef } from 'react';
import { Account, AccountType, GuestInHouseReportData, LoyaltyMembership } from '@mews-commander/data-access';
import { ElementOrElementGetter, Position } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { Popover } from '@mews/b2b-ui';
import { ReservationSimpleDetail } from '../../../../simpleDetail/containers';
import { NotesContainer } from './notes';
import { ReservationsTable, ReservationsTableProps } from './table';
import { AccountsRecord } from './types';
import { LoyaltyMembershipTagList, OtherCustomerClassifications } from './common';
import { ProductsContainer } from './products';

type AdditionalInfoStateType = 'notes' | 'products' | 'reservation';
interface AdditionalInfoState {
    accounts: AccountsRecord;
    data: GuestInHouseReportData;
    type: AdditionalInfoStateType;
}
type PopoverState =
    | {
          account: Account;
          type: 'classifications';
      }
    | {
          loyaltyMemberships: Array<LoyaltyMembership>;
          type: 'loyalty';
      };
type ReservationsContentProps = Pick<ReservationsTableProps, 'filterValues'>;

export const ReservationsContent = ({ filterValues }: ReservationsContentProps) => {
    const [additionalInfoState, setAdditionalInfoState] = useState<AdditionalInfoState | undefined>();
    const [popoverState, setPopoverState] = useState<PopoverState | undefined>();
    const anchorElementRef = useRef<ElementOrElementGetter | undefined>();

    const handleNotesClick = useCallback(
        (data: GuestInHouseReportData, accounts: AccountsRecord) => {
            setAdditionalInfoState({ data, type: 'notes', accounts });
        },
        [setAdditionalInfoState]
    );
    const handleProductsClick = useCallback(
        (data: GuestInHouseReportData, accounts: AccountsRecord) => {
            setAdditionalInfoState({ data, type: 'products', accounts });
        },
        [setAdditionalInfoState]
    );
    const handleReservationInfoClick = useCallback(
        (data: GuestInHouseReportData, accounts: AccountsRecord) => {
            setAdditionalInfoState({ data, type: 'reservation', accounts });
        },
        [setAdditionalInfoState]
    );
    const handleAdditionalInfoClose = () => {
        setAdditionalInfoState(undefined);
    };

    const handleClassificationsClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>, account: Account) => {
            if (event.currentTarget === anchorElementRef.current) return;

            event.nativeEvent.stopImmediatePropagation();
            anchorElementRef.current = event.currentTarget;
            setPopoverState({ account, type: 'classifications' });
        },
        [setPopoverState]
    );
    const handleLoyaltyMembershipsClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>, loyaltyMemberships: Array<LoyaltyMembership>) => {
            if (event.currentTarget === anchorElementRef.current) return;

            event.nativeEvent.stopImmediatePropagation();
            anchorElementRef.current = event.currentTarget;
            setPopoverState({ loyaltyMemberships, type: 'loyalty' });
        },
        [setPopoverState]
    );
    const handlePopoverClose = useCallback(() => {
        anchorElementRef.current = undefined;
        setPopoverState(undefined);
    }, [setPopoverState]);

    return (
        <>
            <ReservationsTable
                filterValues={filterValues}
                handleClassificationsClick={handleClassificationsClick}
                handleLoyaltyMembershipsClick={handleLoyaltyMembershipsClick}
                handleNotesClick={handleNotesClick}
                handleProductsClick={handleProductsClick}
                handleReservationInfoClick={handleReservationInfoClick}
            />
            <NotesContainer
                onClose={handleAdditionalInfoClose}
                {...(additionalInfoState?.type === 'notes'
                    ? {
                          accountsRecord: pickAccounts(
                              additionalInfoState.data.companionIds,
                              additionalInfoState.accounts
                          ),
                          opened: true,
                          ...getCommonProps(additionalInfoState),
                      }
                    : { opened: false })}
            />
            <ProductsContainer
                onClose={handleAdditionalInfoClose}
                {...(additionalInfoState?.type === 'products'
                    ? {
                          opened: true,
                          productIds: additionalInfoState.data.productIds,
                          ...getCommonProps(additionalInfoState),
                      }
                    : { opened: false })}
            />
            {/* @ts-expect-error https://mews.myjetbrains.com/youtrack/issue/kanban-4230/Types-for-Commander-Sidebar */}
            <ReservationSimpleDetail
                onClose={handleAdditionalInfoClose}
                showTitle
                {...(additionalInfoState?.type === 'reservation'
                    ? {
                          opened: true,
                          resourceUse: {
                              customer: getReservationOwnerCustomerAccount(additionalInfoState),
                              id: additionalInfoState.data.reservationId,
                              groupId: '',
                          },
                      }
                    : {
                          opened: false,
                      })}
            />
            <Popover
                active={!!popoverState}
                anchorElement={anchorElementRef.current}
                onClose={handlePopoverClose}
                title={
                    popoverState?.type === 'classifications'
                        ? lt('Classifications')
                        : lt('GuestInHouseReportColumnLoyaltyPrograms')
                }
                position={Position.Top}
            >
                {popoverState?.type === 'classifications' &&
                popoverState.account.data.discriminator === AccountType.Customer ? (
                    <OtherCustomerClassifications classifications={popoverState.account.data.value.classifications} />
                ) : null}
                {popoverState?.type === 'loyalty' ? (
                    <LoyaltyMembershipTagList loyaltyMemberships={popoverState.loyaltyMemberships} />
                ) : null}
            </Popover>
        </>
    );
};

const pickAccounts = (accountIds: string[], accounts: AccountsRecord) =>
    accountIds.reduce<AccountsRecord>((result, accountId) => {
        result[accountId] = accounts[accountId];

        return result;
    }, {});

const getReservationOwnerCustomerAccount = ({ data, accounts }: AdditionalInfoState) =>
    accounts[data.ownerId]?.data.value || {};

const getCommonProps = (additionalInfoState: AdditionalInfoState) => ({
    ownerAccount: pickAccounts([additionalInfoState.data.ownerId], additionalInfoState.accounts)[
        additionalInfoState.data.ownerId
    ],
    startDate: additionalInfoState.data.startUtc,
    endDate: additionalInfoState.data.endUtc,
    ownerId: additionalInfoState.data.ownerId,
    reservationId: additionalInfoState.data.reservationId,
    resourceId: additionalInfoState.data.assignedResourceId,
});
